import { Component, OnDestroy, OnInit } from '@angular/core';
import { catchError, finalize } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';
import { HotToastService } from '@ngneat/hot-toast';

import { DemoService } from './../../../../demo.service';
import { LegalDateTimeService } from './../../../../services/legal-datetime.service';
import {
  ActivityMetricsV2AgentRollup,
  ActivityMetricsV2Breakdown,
  ActivityMetricsV2Event,
  ActivityMetricsV2ExportResponse,
  ActivityMetricsV2Response,
  ActivityMetricsV2Search,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2SubjectRollup,
} from './../../../../shared/models/activity-metrics-v2.model';
import {
  ACTIVITY_METRICS_V2_DASHBOARD_INDEXED_GROUP_BY,
  ACTIVITY_METRICS_V2_DEFAULT_SOURCES,
  ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
  ActivityMetricsV2CoveragePresentation,
  applyActivityMetricsV2IndexedWindowToRequest,
  buildActivityMetricsV2CoveragePresentation,
  getActivityMetricsV2PreviousFullMonthWindow,
  resolveActivityMetricsV2IndexedThroughUtc,
} from './../activity-metrics-v2-indexed-reporting.util';

interface IndexedDashboardSummaryCard {
  label: string;
  value: number;
  helper: string;
  colour: string;
}

interface IndexedDashboardBreakdownTable {
  key: string;
  label: string;
  rows: Array<{
    label: string;
    count: number;
    event_count: number | null | undefined;
    attempt_count: number | null | undefined;
    rate: number | null | undefined;
  }>;
}

interface TimelineLimitOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-agent-performance-activity-metrics-dashboard-indexed',
  templateUrl: './agent-performance-activity-metrics-dashboard-indexed.component.html',
  styleUrls: ['./agent-performance-activity-metrics-dashboard-indexed.component.scss']
})
export class AgentPerformanceActivityMetricsDashboardIndexedComponent implements OnInit, OnDestroy {
  readonly backendTimelineRowCap = 5000;
  readonly brand_fields: Object = { text: 'NameAndAddress', value: 'brand_id' };
  readonly dateFormat = 'dd/MM/yyyy hh:mm a';
  readonly defaultActivitySources = [...ACTIVITY_METRICS_V2_DEFAULT_SOURCES];
  readonly defaultGroupBy = [...ACTIVITY_METRICS_V2_DASHBOARD_INDEXED_GROUP_BY];
  readonly timelineLimitOptions: TimelineLimitOption[] = [
    { label: '100', value: 100 },
    { label: '250', value: 250 },
    { label: '500', value: 500 },
    { label: 'All (max 5000)', value: 5000 },
  ];
  readonly quickRangeDays = [1, 7, 14, 30, 90, 180, 365, 730];
  readonly monthOptions = [
    { index: 0, label: 'Jan' },
    { index: 1, label: 'Feb' },
    { index: 2, label: 'Mar' },
    { index: 3, label: 'Apr' },
    { index: 4, label: 'May' },
    { index: 5, label: 'Jun' },
    { index: 6, label: 'Jul' },
    { index: 7, label: 'Aug' },
    { index: 8, label: 'Sep' },
    { index: 9, label: 'Oct' },
    { index: 10, label: 'Nov' },
    { index: 11, label: 'Dec' },
  ];

  search_model: ActivityMetricsV2Search = this.createDefaultSearch();
  response: ActivityMetricsV2Response | null = null;
  store_status: ActivityMetricsV2StoreStatus | null = null;
  report_error_message: string | null = null;
  loading = false;
  loading_started_at_ms: number | null = null;
  loading_elapsed_seconds = 0;
  loading_timer: ReturnType<typeof setInterval> | null = null;
  exporting = false;
  saving = false;
  store_status_loading = false;
  showAdvancedFilters = false;
  activeQuickRangeDays: number | null = null;
  selectedCalendarYear = new Date().getFullYear();
  activeMonthIndex: number | null = null;
  usingAnyIndexedDate = false;
  last_executed_request_model: ActivityMetricsV2Search | null = null;

  picker_when_from: Date | null = null;
  picker_when_to: Date | null = null;

  brands_agent: any[] = [];
  brands_client: any[] = [];
  lookup_warning_messages: string[] = [];

  constructor(
    private readonly demoService: DemoService,
    public readonly legalDate: LegalDateTimeService,
    private readonly toastr: HotToastService,
  ) {}

  ngOnInit(): void {
    this.applyDefaultMonthWindow();
    this.loadLookups();
    this.loadStoreStatus();
  }

  ngOnDestroy(): void {
    this.stopLoadingTimer();
  }

  createDefaultSearch(): ActivityMetricsV2Search {
    return {
      when_from: null,
      when_to: null,
      limit: ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
      projection: 'ui',
      backend_mode: 'event_store',
      activity_sources: [...this.defaultActivitySources],
      brand_id_group: [],
      brand_report_id_group: [],
      subject_number_group: [],
      subject_number: null,
      subject_id: null,
      job_id: null,
      job_type_id_group: [],
      subject_service_type_id_group: [],
      subject_service_resolution_outcome_id_group: [],
      subject_service_outcome_id_group: [],
      state_group: [],
      postcode_group: [],
      suburb_group: [],
      group_by: [...this.defaultGroupBy],
      include_breakdowns: true,
      export_profile_key: 'timeline_audit',
      saved_file_name: 'agent-dashboard-indexed-2-v2',
    };
  }

  loadLookups(): void {
    this.lookup_warning_messages = [];

    this.loadArrayLookup('Agent brands / groups', this.demoService.getBrandsComboBox({ brand_type_id: 3 }), (items) => {
      this.brands_agent = items;
    });
    this.loadArrayLookup('Reporting clients', this.demoService.getBrandsComboBox({ brand_type_id: 2 }), (items) => {
      this.brands_client = items;
    });
  }

  async loadMetrics(): Promise<void> {
    const requestModel = this.buildRequestModel();
    this.loading = true;
    this.loading_started_at_ms = Date.now();
    this.loading_elapsed_seconds = 0;
    this.startLoadingTimer();
    this.report_error_message = null;
    this.last_executed_request_model = null;

    try {
      const status = await this.fetchStoreStatus(requestModel);
      const coveragePresentation = this.buildCoveragePresentation(requestModel, status);
      if (
        !coveragePresentation.hasIndexedCoverage
        || !coveragePresentation.effectiveWindowFromUtc
        || !coveragePresentation.effectiveWindowToUtc
      ) {
        this.response = null;
        this.report_error_message = coveragePresentation.detail;
        return;
      }

      const executionRequestModel = applyActivityMetricsV2IndexedWindowToRequest(requestModel, coveragePresentation);
      const response = await firstValueFrom(this.demoService.getAgentActivityMetricsV2(executionRequestModel));
      this.response = response;
      this.report_error_message = null;
      this.last_executed_request_model = executionRequestModel;
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to load indexed Dashboard 2.0.';
    } finally {
      this.loading = false;
      this.loading_started_at_ms = null;
      this.stopLoadingTimer();
    }
  }

  resetSearch(): void {
    this.search_model = this.createDefaultSearch();
    this.applyDefaultMonthWindow();
    this.loadStoreStatus();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyQuickRange(days: number, reload = true): void {
    const to = this.legalDate.toPickerDate(new Date()) ?? new Date();
    const from = new Date(to.getTime());
    from.setDate(from.getDate() - days);

    this.activeQuickRangeDays = days;
    this.usingAnyIndexedDate = false;
    this.picker_when_from = from;
    this.picker_when_to = to;
    this.syncCalendarSelection();

    if (reload) {
      void this.loadMetrics();
    }
  }

  applyDefaultMonthWindow(): void {
    const defaultWindow = getActivityMetricsV2PreviousFullMonthWindow(this.legalDate.toPickerDate(new Date()) ?? new Date());
    this.selectedCalendarYear = defaultWindow.year;
    this.activeQuickRangeDays = null;
    this.activeMonthIndex = defaultWindow.monthIndex;
    this.usingAnyIndexedDate = false;
    this.picker_when_from = defaultWindow.from;
    this.picker_when_to = defaultWindow.to;
    this.syncCalendarSelection();
  }

  async applyAnyIndexedDate(reload = true): Promise<void> {
    this.report_error_message = null;

    try {
      const status = await this.fetchStoreStatus(this.buildRequestModel());
      const earliestIndexedUtc = status?.earliest_indexed_utc ?? null;
      const indexedThroughUtc = resolveActivityMetricsV2IndexedThroughUtc(status, this.defaultActivitySources);

      if (!earliestIndexedUtc || !indexedThroughUtc) {
        this.report_error_message = 'Any Indexed Date is not available yet because the indexed date bounds could not be resolved for the selected source set.';
        return;
      }

      const whenFrom = this.legalDate.toPickerDate(earliestIndexedUtc);
      const whenTo = this.legalDate.toPickerDate(indexedThroughUtc);

      if (!whenFrom || !whenTo) {
        this.report_error_message = 'Any Indexed Date could not convert the indexed date bounds into a usable reporting window.';
        return;
      }

      this.activeQuickRangeDays = null;
      this.activeMonthIndex = null;
      this.usingAnyIndexedDate = true;
      this.search_model.limit = Math.min(this.search_model.limit || ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT, ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT);
      this.picker_when_from = whenFrom;
      this.picker_when_to = whenTo;
      this.syncCalendarSelection();
      this.activeMonthIndex = null;

      if (reload) {
        await this.loadMetrics();
      } else {
        this.loadStoreStatus(this.buildRequestModel());
      }
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to resolve Any Indexed Date for indexed dashboard reporting.';
    }
  }

  setMonthWindow(monthIndex: number): void {
    const whenFrom = new Date(this.selectedCalendarYear, monthIndex, 1, 0, 0, 0, 0);
    const whenTo = new Date(this.selectedCalendarYear, monthIndex + 1, 0, 23, 59, 0, 0);

    this.activeQuickRangeDays = null;
    this.activeMonthIndex = monthIndex;
    this.usingAnyIndexedDate = false;
    this.picker_when_from = whenFrom;
    this.picker_when_to = whenTo;
  }

  onCalendarYearChange(year: number | string): void {
    const numericYear = Number(year);
    if (!Number.isFinite(numericYear)) {
      return;
    }

    this.selectedCalendarYear = numericYear;

    if (this.activeMonthIndex !== null) {
      this.setMonthWindow(this.activeMonthIndex);
    }
  }

  onPickerChange(): void {
    this.activeQuickRangeDays = null;
    this.usingAnyIndexedDate = false;
    this.syncCalendarSelection();
  }

  async exportMetrics(): Promise<void> {
    const requestModel: ActivityMetricsV2Search = {
      ...this.buildRequestModel(),
      projection: 'audit',
    };

    this.exporting = true;
    try {
      const status = await this.fetchStoreStatus(requestModel);
      const coveragePresentation = this.buildCoveragePresentation(requestModel, status);
      if (
        !coveragePresentation.hasIndexedCoverage
        || !coveragePresentation.effectiveWindowFromUtc
        || !coveragePresentation.effectiveWindowToUtc
      ) {
        this.report_error_message = coveragePresentation.detail;
        return;
      }

      const executionRequestModel = applyActivityMetricsV2IndexedWindowToRequest(requestModel, coveragePresentation);
      const response = await firstValueFrom(this.demoService.exportAgentActivityMetricsV2(executionRequestModel));
      this.downloadExportFile(response);
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to export indexed Dashboard 2.0.';
    } finally {
      this.exporting = false;
    }
  }

  saveFootprint(): void {
    if (!this.response) {
      this.report_error_message = 'Load indexed data before saving a footprint.';
      return;
    }

    if (!window.confirm('Save indexed dashboard metrics as a footprint?')) {
      return;
    }

    const requestModel = this.last_executed_request_model ?? this.buildRequestModel();
    this.saving = true;
    this.demoService.saveAgentMetricsData({
      data: this.response,
      search_model: requestModel,
      saved_file_name: this.search_model.saved_file_name || 'agent-dashboard-indexed-2-v2',
    })
      .pipe(finalize(() => {
        this.saving = false;
      }))
      .subscribe({
        next: () => {
          this.toastr.success('Indexed dashboard footprint saved.');
        },
        error: (error) => {
          this.report_error_message = error?.error?.detail || error?.message || 'Failed to save indexed dashboard footprint.';
        },
      });
  }

  loadStoreStatus(requestModel?: ActivityMetricsV2Search | null): void {
    this.store_status_loading = true;

    const statusRequest = requestModel === null
      ? this.demoService.getAgentActivityMetricsV2StoreStatus()
      : this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel ?? this.buildRequestModel());

    statusRequest
      .pipe(finalize(() => {
        this.store_status_loading = false;
      }))
      .subscribe({
        next: (status) => {
          this.store_status = status;
        },
        error: () => {
          this.store_status = null;
        },
      });
  }

  buildRequestModel(): ActivityMetricsV2Search {
    return {
      ...this.search_model,
      backend_mode: 'event_store',
      when_from: this.picker_when_from ? this.legalDate.fromPickerDate(this.picker_when_from) : null,
      when_to: this.picker_when_to ? this.legalDate.fromPickerDate(this.picker_when_to) : null,
      activity_sources: [...this.defaultActivitySources],
      group_by: [...this.defaultGroupBy],
    };
  }

  get calendarYearOptions(): number[] {
    const currentYear = this.legalDate.toPickerDate(new Date())?.getFullYear() ?? new Date().getFullYear();
    const earliestYear = Math.max(2020, currentYear - 8);
    const years: number[] = [];

    for (let year = currentYear; year >= earliestYear; year -= 1) {
      years.push(year);
    }

    return years;
  }

  get coveragePresentation() {
    return this.buildCoveragePresentation(this.buildRequestModel(), this.store_status);
  }

  get indexedThroughUtc(): string | null {
    return resolveActivityMetricsV2IndexedThroughUtc(this.store_status, this.defaultActivitySources);
  }

  get earliestIndexedUtc(): string | null {
    return this.store_status?.earliest_indexed_utc ?? null;
  }

  get indexedStatusSummary(): string {
    if (this.store_status_loading) {
      return 'Checking indexed coverage for the selected window...';
    }

    if (!this.store_status) {
      return 'Indexed coverage has not been checked yet for the selected window.';
    }

    return this.coveragePresentation.detail;
  }

  get backendStatusAlertClass(): string {
    if (this.loading) {
      return 'alert alert-warning classic-runtime-alert';
    }

    if (this.report_error_message && !this.response) {
      return 'alert alert-danger classic-runtime-alert';
    }

    return this.coveragePresentation.alertClass;
  }

  get summaryCards(): IndexedDashboardSummaryCard[] {
    const summary = this.response?.summary;
    if (!summary) {
      return [];
    }

    return [
      { label: 'Events', value: summary.total_events, helper: `${summary.source_counts.length} active sources`, colour: '#457EDD' },
      { label: 'Attempts', value: summary.total_attempts, helper: 'Countable attempt/service rows', colour: '#1F1F1F' },
      { label: 'Subjects', value: summary.total_subjects, helper: `${summary.total_jobs} jobs admitted`, colour: '#A2D369' },
      { label: 'Jobs', value: summary.total_jobs, helper: `${summary.total_agents} agents active`, colour: '#FA6F50' },
      { label: 'Agents', value: summary.total_agents, helper: `${summary.total_regions} regions resolved`, colour: '#FECF56' },
      { label: 'Regions', value: summary.total_regions, helper: 'Resolved region footprint', colour: '#62ADE5' },
    ];
  }

  get totalEvents(): number {
    return this.response?.summary?.total_events ?? 0;
  }

  get totalSubjects(): number {
    return this.response?.summary?.total_subjects ?? 0;
  }

  get totalAgents(): number {
    return this.response?.summary?.total_agents ?? 0;
  }

  get sourceCountLabels(): string[] {
    return (this.response?.summary?.source_counts ?? []).map((sourceCount) => `${sourceCount.label} ${sourceCount.count}`);
  }

  get breakdownTables(): IndexedDashboardBreakdownTable[] {
    return [
      this.mapBreakdownTable('service_type', 'Service Type'),
      this.mapBreakdownTable('resolution_outcome', 'Resolution Outcome'),
      this.mapBreakdownTable('resolution_result', 'Resolution Result'),
      this.mapBreakdownTable('state', 'State'),
      this.mapBreakdownTable('suburb_region', 'Suburb + Region'),
      this.mapBreakdownTable('attempt_lifecycle', 'Attempt Lifecycle'),
    ].filter((table): table is IndexedDashboardBreakdownTable => !!table);
  }

  get subjectRows(): ActivityMetricsV2SubjectRollup[] {
    return (this.response?.subjects ?? [])
      .slice()
      .sort((left, right) => (right.event_count ?? 0) - (left.event_count ?? 0))
      .slice(0, 30);
  }

  get agentRows(): ActivityMetricsV2AgentRollup[] {
    return (this.response?.agents ?? [])
      .slice()
      .sort((left, right) => (right.event_count ?? 0) - (left.event_count ?? 0))
      .slice(0, 30);
  }

  get timelineRows(): ActivityMetricsV2Event[] {
    return (this.response?.timeline ?? []).slice(0, this.search_model.limit);
  }

  get hasLoadedReport(): boolean {
    return !!this.response;
  }

  get resolvedBackendLabel(): string {
    return 'Reporting Tables';
  }

  get backendRuntimeSummary(): string {
    const summaryParts = [
      this.usingAnyIndexedDate
        ? 'Any Indexed Date'
        : `Default window ${this.estimateWindowDays()} day${this.estimateWindowDays() === 1 ? '' : 's'}`,
      'Requested Reporting Tables',
      `Resolved ${this.resolvedBackendLabel}`,
      this.search_model.limit >= this.backendTimelineRowCap
        ? `Timeline rows all (cap ${this.backendTimelineRowCap})`
        : `Timeline rows ${this.search_model.limit}`,
    ];

    if (this.store_status?.coverage?.coverage_status) {
      summaryParts.splice(3, 0, `Coverage ${this.store_status.coverage.coverage_status}`);
    }

    return summaryParts.join(' | ');
  }

  get loadingStatusMessage(): string {
    const sourceCount = this.search_model.activity_sources?.length ?? 0;
    const daySpan = this.estimateWindowDays();
    return `Fetching indexed activity across ${sourceCount} source${sourceCount === 1 ? '' : 's'} for roughly ${daySpan} day${daySpan === 1 ? '' : 's'}.`;
  }

  get loadingStatusDetail(): string {
    return `${this.backendRuntimeSummary} | Projection ${this.search_model.projection?.toUpperCase?.() || 'UI'}`;
  }

  get durationLabel(): string {
    if (!this.picker_when_from || !this.picker_when_to) {
      return '-';
    }

    const totalMs = this.picker_when_to.getTime() - this.picker_when_from.getTime();
    if (totalMs <= 0) {
      return '0m';
    }

    const totalMinutes = Math.floor(totalMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} D`);
    }
    if (hours > 0) {
      parts.push(`${hours} H`);
    }
    if (minutes > 0 || !parts.length) {
      parts.push(`${minutes} M`);
    }

    return parts.join(' ');
  }

  quickRangeLabel(days: number): string {
    return days === 1 ? 'Past 24 hrs' : days === 730 ? 'Past 2 YRS' : `Past ${days} Days`;
  }

  trackByCard(index: number, item: IndexedDashboardSummaryCard): string {
    return item.label;
  }

  trackBySubject(index: number, item: ActivityMetricsV2SubjectRollup): string {
    return item.subject_number || `${item.subject_id || index}`;
  }

  trackByAgent(index: number, item: ActivityMetricsV2AgentRollup): string {
    return `${item.agent_id || index}-${item.agent_name || ''}`;
  }

  trackByEvent(index: number, item: ActivityMetricsV2Event): string {
    return item.event_id;
  }

  private loadArrayLookup<T>(label: string, request$: Observable<T>, assign: (items: any[]) => void): void {
    request$
      .pipe(
        catchError((error) => {
          const detail = error?.error?.detail || error?.message || 'Lookup request failed.';
          this.lookup_warning_messages = [...this.lookup_warning_messages, `${label}: ${detail}`];
          assign([]);
          return of([]);
        })
      )
      .subscribe((result) => {
        assign(Array.isArray(result) ? result : []);
      });
  }

  private async fetchStoreStatus(requestModel: ActivityMetricsV2Search): Promise<ActivityMetricsV2StoreStatus | null> {
    this.store_status_loading = true;

    try {
      const status = await firstValueFrom(this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel));
      this.store_status = status;
      return status;
    } finally {
      this.store_status_loading = false;
    }
  }

  private buildCoveragePresentation(
    requestModel: ActivityMetricsV2Search,
    status: ActivityMetricsV2StoreStatus | null,
  ): ActivityMetricsV2CoveragePresentation {
    return buildActivityMetricsV2CoveragePresentation({
      status,
      requestedSources: requestModel.activity_sources,
      requestedWhenFrom: requestModel.when_from,
      requestedWhenTo: requestModel.when_to,
      formatDate: (value: string) => this.legalDate.formatAudit(value, 'DD/MM/YYYY h:mm a'),
      formatDuration: (value: number) => this.formatDuration(value),
    });
  }

  private startLoadingTimer(): void {
    this.stopLoadingTimer();
    this.loading_timer = setInterval(() => {
      if (!this.loading_started_at_ms) {
        this.loading_elapsed_seconds = 0;
        return;
      }

      this.loading_elapsed_seconds = Math.max(0, Math.floor((Date.now() - this.loading_started_at_ms) / 1000));
    }, 1000);
  }

  private stopLoadingTimer(): void {
    if (this.loading_timer) {
      clearInterval(this.loading_timer);
      this.loading_timer = null;
    }
  }

  private syncCalendarSelection(): void {
    const whenFrom = this.picker_when_from;
    const whenTo = this.picker_when_to;

    if (!whenFrom || !whenTo) {
      this.activeMonthIndex = null;
      return;
    }

    this.selectedCalendarYear = whenFrom.getFullYear();

    const isSameMonth = whenFrom.getFullYear() === whenTo.getFullYear()
      && whenFrom.getMonth() === whenTo.getMonth();
    const isMonthStart = whenFrom.getDate() === 1
      && whenFrom.getHours() === 0
      && whenFrom.getMinutes() === 0;
    const expectedMonthEnd = new Date(whenFrom.getFullYear(), whenFrom.getMonth() + 1, 0, 23, 59, 0, 0);
    const isMonthEnd = whenTo.getFullYear() === expectedMonthEnd.getFullYear()
      && whenTo.getMonth() === expectedMonthEnd.getMonth()
      && whenTo.getDate() === expectedMonthEnd.getDate()
      && whenTo.getHours() === expectedMonthEnd.getHours()
      && whenTo.getMinutes() === expectedMonthEnd.getMinutes();

    this.activeMonthIndex = isSameMonth && isMonthStart && isMonthEnd
      ? whenFrom.getMonth()
      : null;
  }

  private estimateWindowDays(): number {
    if (!this.picker_when_from || !this.picker_when_to) {
      return 0;
    }

    const fromTime = this.picker_when_from.getTime();
    const toTime = this.picker_when_to.getTime();
    if (!Number.isFinite(fromTime) || !Number.isFinite(toTime) || toTime < fromTime) {
      return 0;
    }

    return Math.max(1, Math.ceil((toTime - fromTime) / 86400000));
  }

  private mapBreakdownTable(key: string, label: string): IndexedDashboardBreakdownTable | null {
    const breakdown = this.response?.grouped_breakdowns?.find((item) => item.breakdown_key === key);
    if (!breakdown?.rows?.length) {
      return null;
    }

    return {
      key,
      label,
      rows: breakdown.rows.slice(0, 20).map((row) => ({
        label: row.label,
        count: row.count,
        event_count: row.event_count,
        attempt_count: row.attempt_count,
        rate: row.rate,
      })),
    };
  }

  private formatDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms <= 0) {
      return '0m';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (!parts.length) {
      parts.push(`${totalSeconds}s`);
    }

    return parts.join(' ');
  }

  private downloadExportFile(response: ActivityMetricsV2ExportResponse): void {
    const rows = Array.isArray(response.rows) ? response.rows : [];
    const columns = Array.isArray(response.columns) ? response.columns : [];

    if (!rows.length || !columns.length) {
      return;
    }

    const csvLines = [
      columns.join(','),
      ...rows.map((row) => columns.map((column) => this.escapeCsvValue(row[column])).join(',')),
    ];

    const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${response.profile?.file_name || this.search_model.saved_file_name || 'activity-metrics-v2-dashboard-indexed'}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = `${value}`.replace(/"/g, '""');
    return `"${stringValue}"`;
  }
}
